import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BuildingIcon, MonitorIcon, HeartHandshakeIcon, ArrowRightIcon } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0,
    x: 30
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
const principles = [{
  num: '1',
  title: 'Reframe with the client, not against them',
  desc: 'Share the research findings early. Reframe success as outcomes (reduced isolation) not outputs (a platform). Use the GOV.UK Service Standard as shared language.'
}, {
  num: '2',
  title: 'Start where trust already lives',
  desc: 'Partner with GP surgeries, libraries, village halls and faith groups. Trusted intermediaries who can introduce the service in person.'
}, {
  num: '3',
  title: 'Design the offline-to-online journey',
  desc: 'Printed event cards → phone helpline → a simple, AA-accessible web service. Each channel reaches a different segment. Digital is one option, not the only option.'
}, {
  num: '4',
  title: 'Prototype fast, test with real users',
  desc: 'Run a discovery sprint with 5 to 8 older adults in a pilot area before writing a line of production code.'
}];
export function SlideTwo() {
  return <motion.div className="w-full h-full overflow-y-auto" variants={container} initial="hidden" animate="visible" exit={{
    opacity: 0,
    x: -30,
    transition: {
      duration: 0.3
    }
  }} data-id="element-66">
      <motion.div variants={item} className="flex justify-between items-center w-full fixed top-6 left-0 px-16 text-[11px] font-medium tracking-[0.2em] text-apple-muted uppercase z-10" data-id="element-67">
        <span data-id="element-68">
          <span className="text-apple-accent" data-id="element-69">02</span>
          <span className="opacity-50" data-id="element-70"> / 02</span>
        </span>
        <span data-id="element-71">Our approach</span>
      </motion.div>

      <div className="w-full max-w-[1280px] mx-auto px-16 pt-24 pb-20 flex flex-col gap-10" data-id="element-72">
        <motion.div variants={item} className="flex flex-col gap-2" data-id="element-73">
          <span className="text-apple-accent text-xs font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-74">
            Our Approach
          </span>
          <h1 className="text-[clamp(32px,4vw,56px)] leading-[1.1] font-semibold tracking-tight text-apple-text" data-id="element-75">
            Build a{' '}
            <span className="text-apple-accent italic font-medium" data-id="element-76">bridge</span>
            , not just a platform
          </h1>
        </motion.div>

        <div className="flex gap-12 items-start" data-id="element-77">
          {/* Left Column: Principles */}
          <motion.div variants={item} className="w-[45%] flex flex-col gap-8" data-id="element-78">
            {principles.map(p => <div key={p.num} className="flex gap-5" data-id="element-79">
                <span className="text-apple-accent font-light text-4xl leading-none shrink-0 opacity-80" data-id="element-80">
                  {p.num}
                </span>
                <div className="flex flex-col gap-1.5 pt-1" data-id="element-81">
                  <h3 className="font-semibold text-apple-text text-base" data-id="element-82">
                    {p.title}
                  </h3>
                  <p className="text-sm text-apple-muted leading-relaxed" data-id="element-83">
                    {p.desc}
                  </p>
                </div>
              </div>)}
          </motion.div>

          {/* Right Column: The Model */}
          <motion.div variants={item} className="w-[55%] flex flex-col gap-6" data-id="element-84">
            <h4 className="text-[10px] font-semibold tracking-[0.2em] text-apple-muted uppercase text-center" data-id="element-85">
              The Blended Service Model
            </h4>

            {/* Bridge Diagram */}
            <div className="flex items-center justify-between w-full" data-id="element-86">
              {/* Stage 1 */}
              <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-5 flex flex-col items-center text-center gap-4 w-[30%]" data-id="element-87">
                <div className="text-apple-accent" data-id="element-88">
                  <HeartHandshakeIcon size={24} data-id="element-89" />
                </div>
                <div className="flex flex-col gap-1" data-id="element-90">
                  <h5 className="text-[10px] font-semibold tracking-[0.15em] text-apple-muted uppercase mb-2" data-id="element-91">
                    Now · Trust
                  </h5>
                  <span className="text-xs text-apple-muted" data-id="element-92">
                    Trusted community groups
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-93">
                    Face-to-face events
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-94">
                    Printed materials
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-95">
                    Phone helpline
                  </span>
                </div>
              </div>

              <ArrowRightIcon className="text-apple-muted/50 shrink-0" size={20} data-id="element-96" />

              {/* Stage 2 */}
              <div className="bg-apple-card shadow-md border border-apple-border/50 ring-1 ring-apple-accent/20 rounded-2xl p-5 flex flex-col items-center text-center gap-4 w-[30%]" data-id="element-97">
                <div className="text-apple-accent" data-id="element-98">
                  <BuildingIcon size={24} data-id="element-99" />
                </div>
                <div className="flex flex-col gap-1" data-id="element-100">
                  <h5 className="text-[10px] font-semibold tracking-[0.15em] text-apple-accent uppercase mb-2" data-id="element-101">
                    Bridge · Access
                  </h5>
                  <span className="text-xs text-apple-muted" data-id="element-102">
                    Assisted digital sessions
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-103">
                    Tablet lending schemes
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-104">
                    Simple accessible site
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-105">
                    Community champions
                  </span>
                </div>
              </div>

              <ArrowRightIcon className="text-apple-muted/50 shrink-0" size={20} data-id="element-106" />

              {/* Stage 3 */}
              <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-5 flex flex-col items-center text-center gap-4 w-[30%]" data-id="element-107">
                <div className="text-apple-muted" data-id="element-108">
                  <MonitorIcon size={24} data-id="element-109" />
                </div>
                <div className="flex flex-col gap-1" data-id="element-110">
                  <h5 className="text-[10px] font-semibold tracking-[0.15em] text-apple-muted uppercase mb-2" data-id="element-111">
                    Future · Digital
                  </h5>
                  <span className="text-xs text-apple-muted" data-id="element-112">
                    Online meetups
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-113">
                    Event discovery
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-114">
                    Peer messaging
                  </span>
                  <span className="text-xs text-apple-muted" data-id="element-115">
                    Based on real usage
                  </span>
                </div>
              </div>
            </div>

            {/* Feedback Loop Callout */}
            <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-5 mt-2 text-center" data-id="element-116">
              <p className="text-sm text-apple-text" data-id="element-117">
                <span className="font-semibold text-apple-accent mr-2" data-id="element-118">
                  Continuous feedback loop:
                </span>
                qualitative research every 4 weeks with users across all three
                channels. We iterate the digital platform based on what we learn
                from the offline experience.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom Provocation */}
        <motion.div variants={item} className="mt-4" data-id="element-119">
          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl px-8 py-5 pl-6 border-l-[4px] border-l-apple-accent inline-block" data-id="element-120">
            <p className="text-base text-apple-text" data-id="element-121">
              <span className="text-lg font-semibold text-apple-accent mr-2" data-id="element-122">
                Measure what matters:
              </span>
              connections made and isolation reduced, not monthly active users
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}